import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gift, Ticket, Shield, Tag, ShoppingBag, Banknote,
  ChevronRight, Loader2, CheckCircle, TrendingUp,
  TrendingDown, Minus, Wallet, ArrowRight, X, Bot, Zap
} from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const ICON_MAP = {
  ticket: Ticket, shield: Shield, tag: Tag,
  'shopping-bag': ShoppingBag, banknote: Banknote,
};

const CATEGORY_COLORS = {
  booking_discount: { bg: 'from-blue-600/20 to-blue-600/5', border: 'border-blue-500/20', text: 'text-blue-400', badge: 'bg-blue-500/20 text-blue-400' },
  insurance: { bg: 'from-emerald-600/20 to-emerald-600/5', border: 'border-emerald-500/20', text: 'text-emerald-400', badge: 'bg-emerald-500/20 text-emerald-400' },
  partner_voucher: { bg: 'from-purple-600/20 to-purple-600/5', border: 'border-purple-500/20', text: 'text-purple-400', badge: 'bg-purple-500/20 text-purple-400' },
  merchandise: { bg: 'from-orange-600/20 to-orange-600/5', border: 'border-orange-500/20', text: 'text-orange-400', badge: 'bg-orange-500/20 text-orange-400' },
  cash_conversion: { bg: 'from-lime-600/20 to-lime-600/5', border: 'border-lime-400/20', text: 'text-lime-400', badge: 'bg-lime-500/20 text-lime-400' },
};

export default function RedeemPage() {
  const { api } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await api.get('/redemption/catalog');
      setCatalog(res.data);
    } catch (err) {
      console.error('Catalog error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/redemption/history?limit=10');
      setHistory(res.data);
    } catch (err) {
      console.error('History error:', err);
    }
  }, [api]);

  useEffect(() => { fetchCatalog(); fetchHistory(); }, [fetchCatalog, fetchHistory]);

  const requestQuote = async (category, item) => {
    setQuoting(true);
    setQuote(null);
    setResult(null);
    try {
      const res = await api.post('/redemption/quote', {
        conversion_type: category.id,
        play_amount: item.play_cost,
        item_id: item.id || null,
      });
      setQuote(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to get quote');
    } finally {
      setQuoting(false);
    }
  };

  const executeRedemption = async () => {
    if (!quote) return;
    setExecuting(true);
    try {
      const res = await api.post('/redemption/execute', { quote_id: quote.quote_id });
      setResult(res.data);
      setQuote(null);
      fetchCatalog();
      fetchHistory();
    } catch (err) {
      alert(err.response?.data?.detail || 'Redemption failed');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const cashRate = catalog?.cash_rate || {};
  const nudges = catalog?.nudges || {};

  return (
    <div data-testid="redeem-page" className="space-y-6">
      {/* Header */}
      <motion.div {...fadeUp} className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
            <Gift size={28} className="text-lime-400" />
            Rewards Store
          </h1>
          <p className="text-slate-400 text-sm font-manrope mt-0.5">Redeem your $PLAY for real value</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="history-toggle-btn"
            onClick={() => setShowHistory(!showHistory)}
            className="px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-xs transition-all"
          >
            {showHistory ? 'Back to Store' : 'History'}
          </button>
          <div className="px-4 py-2 rounded-full bg-blue-600/20 border border-blue-500/20">
            <span className="text-blue-400 font-barlow font-bold">{catalog?.balance || 0} $PLAY</span>
          </div>
        </div>
      </motion.div>

      {/* Success Result */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            className="bg-lime-500/10 border border-lime-400/30 rounded-xl p-5 shadow-[0_0_25px_rgba(190,242,100,0.1)]"
            data-testid="redemption-success"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-lime-400" />
                <span className="font-barlow text-lg font-bold text-lime-400">Redemption Complete!</span>
              </div>
              <button onClick={() => setResult(null)} className="text-slate-400 hover:text-white"><X size={16} /></button>
            </div>
            <p className="text-white text-sm mb-2">{result.message}</p>
            <div className="flex gap-4 text-xs text-slate-400">
              <span>{result.play_burned} $PLAY burned</span>
              {result.fiat_value > 0 && <span>Value: ₱{result.fiat_value}</span>}
              {result.voucher_code && <span className="text-lime-400 font-mono">Code: {result.voucher_code}</span>}
              {result.policy_ref && <span className="font-mono">{result.policy_ref}</span>}
              {result.order_ref && <span className="font-mono">{result.order_ref}</span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quote Modal */}
      <AnimatePresence>
        {quote && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            className="bg-slate-950/80 backdrop-blur-xl border border-blue-500/30 rounded-xl p-5 shadow-[0_0_30px_rgba(59,130,246,0.15)]"
            data-testid="redemption-quote"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs text-blue-400 uppercase tracking-wider font-bold">Conversion Quote</span>
              <span className="text-xs text-slate-500 ml-auto">Expires in 10 min</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">You Pay</p>
                <p className="font-barlow text-3xl font-bold text-white">{quote.play_amount} <span className="text-blue-400 text-lg">$PLAY</span></p>
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">You Get</p>
                <p className="font-barlow text-3xl font-bold text-lime-400">₱{quote.fiat_value}</p>
                <p className="text-slate-500 text-xs">{quote.description}</p>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                data-testid="confirm-redeem-btn"
                onClick={executeRedemption}
                disabled={executing}
                className="px-6 py-2.5 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-bold text-sm uppercase tracking-wide transition-all disabled:opacity-50 shadow-[0_0_20px_rgba(190,242,100,0.3)]"
              >
                {executing ? <><Loader2 size={14} className="animate-spin inline mr-1" /> Processing...</> : 'Confirm Redemption'}
              </button>
              <button onClick={() => setQuote(null)}
                className="px-6 py-2.5 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-sm transition-all"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History View */}
      {showHistory ? (
        <motion.div {...fadeUp} className="space-y-3" data-testid="redemption-history">
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">Redemption History</h3>
          {history.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No redemptions yet</div>
          ) : (
            history.map((h, i) => (
              <div key={h.id || i} className="bg-slate-950/60 border border-white/10 rounded-xl p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  h.status === 'executed' ? 'bg-lime-500/20 text-lime-400' : 'bg-slate-500/20 text-slate-400'
                }`}>
                  <Gift size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{h.description}</p>
                  <p className="text-slate-500 text-xs">{new Date(h.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-pink-400 font-barlow font-bold text-sm">-{h.play_amount} $PLAY</p>
                  <p className="text-slate-500 text-[10px]">{h.status}</p>
                </div>
              </div>
            ))
          )}
        </motion.div>
      ) : (
        <>
          {/* AI Nudge Banner */}
          {nudges?.featured_nudge && (
            <motion.div {...fadeUp} transition={{ delay: 0.1 }}
              className="bg-gradient-to-r from-blue-600/15 to-lime-500/10 border border-blue-500/20 rounded-xl p-5 relative overflow-hidden"
              data-testid="redemption-nudge"
            >
              <div className="absolute top-2 right-3 flex items-center gap-1">
                <Bot size={12} className="text-blue-400" />
                <span className="text-[10px] text-blue-400 font-mono">AI Suggestion</span>
              </div>
              <h3 className="font-barlow text-lg font-bold text-white mb-1">{nudges.featured_nudge.title}</h3>
              <p className="text-slate-300 text-sm">{nudges.featured_nudge.message}</p>
              {nudges.featured_nudge.savings_estimate && (
                <p className="text-lime-400 text-sm font-bold mt-2">{nudges.featured_nudge.savings_estimate}</p>
              )}
              {nudges.conversion_tip && (
                <p className="text-slate-500 text-xs mt-2 flex items-center gap-1">
                  <Zap size={10} /> {nudges.conversion_tip}
                </p>
              )}
            </motion.div>
          )}

          {/* Cash Rate Indicator */}
          <motion.div {...fadeUp} transition={{ delay: 0.15 }}
            className="bg-slate-950/60 border border-white/10 rounded-xl p-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-lg bg-lime-500/20 flex items-center justify-center text-lime-400">
              <Banknote size={20} />
            </div>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Cash Conversion Rate</p>
              <p className="text-slate-400 text-xs">1,000 $PLAY = ₱{cashRate.rate_per_1000 || 85} (Corridor: ₱{cashRate.corridor?.min}-₱{cashRate.corridor?.max})</p>
            </div>
            <div className="flex items-center gap-1">
              {cashRate.trend === 'increasing' ? <TrendingUp size={14} className="text-lime-400" /> :
               cashRate.trend === 'decreasing' ? <TrendingDown size={14} className="text-pink-400" /> :
               <Minus size={14} className="text-slate-400" />}
              <span className={`text-xs font-bold capitalize ${
                cashRate.trend === 'increasing' ? 'text-lime-400' : cashRate.trend === 'decreasing' ? 'text-pink-400' : 'text-slate-400'
              }`}>{cashRate.trend || 'stable'}</span>
            </div>
          </motion.div>

          {/* Categories Grid */}
          <div className="space-y-6">
            {(catalog?.categories || []).map((cat, ci) => {
              const colors = CATEGORY_COLORS[cat.id] || CATEGORY_COLORS.booking_discount;
              const IconComp = ICON_MAP[cat.icon] || Gift;
              const isOpen = selectedCategory === cat.id;

              return (
                <motion.div key={cat.id} {...fadeUp} transition={{ delay: 0.1 + ci * 0.05 }}>
                  {/* Category Header */}
                  <button
                    data-testid={`category-${cat.id}`}
                    onClick={() => setSelectedCategory(isOpen ? null : cat.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r ${colors.bg} border ${colors.border} hover:scale-[1.01] transition-all text-left`}
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colors.badge}`}>
                      <IconComp size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-barlow text-base font-bold text-white">{cat.name}</h3>
                      <p className="text-slate-400 text-xs">{cat.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] text-slate-500 hidden md:block">{cat.items?.length || 0} options</span>
                      <ChevronRight size={18} className={`${colors.text} transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    </div>
                  </button>

                  {/* Category Items */}
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                          {(cat.items || []).map((item, ii) => {
                            const canAfford = (catalog?.balance || 0) >= item.play_cost;
                            return (
                              <div key={item.id || ii}
                                className={`bg-slate-950/60 border border-white/10 rounded-xl p-4 transition-all ${
                                  canAfford ? 'hover:border-blue-500/30 cursor-pointer' : 'opacity-50'
                                }`}
                                data-testid={`redeem-item-${cat.id}-${ii}`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="text-white text-sm font-medium flex-1">{item.name}</h4>
                                  {item.partner && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">{item.partner}</span>}
                                </div>
                                {item.description && <p className="text-slate-500 text-xs mb-2">{item.description}</p>}
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-barlow text-lg font-bold text-white">{item.play_cost} <span className="text-blue-400 text-xs">$PLAY</span></p>
                                    {item.face_value_php && <p className="text-slate-500 text-[10px]">Value: ₱{item.face_value_php}</p>}
                                    {item.fiat_value && <p className="text-lime-400 text-xs">= ₱{item.fiat_value}</p>}
                                    {item.price_php && <p className="text-slate-500 text-[10px]">Session: ₱{item.price_php}</p>}
                                  </div>
                                  <button
                                    data-testid={`redeem-btn-${cat.id}-${ii}`}
                                    onClick={() => canAfford && requestQuote(cat, item)}
                                    disabled={!canAfford || quoting}
                                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${
                                      canAfford
                                        ? 'bg-blue-600/20 hover:bg-blue-600/40 text-blue-400'
                                        : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                                    }`}
                                  >
                                    {quoting ? <Loader2 size={14} className="animate-spin" /> : canAfford ? 'Redeem' : 'Low Balance'}
                                  </button>
                                </div>
                                {item.inventory !== undefined && (
                                  <p className="text-slate-600 text-[10px] mt-1">{item.inventory} remaining</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Secondary Nudges */}
          {nudges?.secondary_nudges?.length > 0 && (
            <motion.div {...fadeUp} transition={{ delay: 0.5 }}>
              <div className="grid md:grid-cols-2 gap-3">
                {nudges.secondary_nudges.map((n, i) => (
                  <div key={i} className="bg-slate-950/60 border border-white/5 rounded-xl p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 flex-shrink-0 mt-0.5">
                      <Bot size={16} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{n.title}</p>
                      <p className="text-slate-400 text-xs mt-0.5">{n.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
